import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useState} from 'react';

import {SIDE_BAR} from '@/src/app/constants/howToStoryblok';
import requestStoryBlok from '@/src/app/utils/requestStoryBlok';
import useIsMobile from '@/src/hooks/useIsMobile';
import BackToList from '@/src/howTo/components/BackToList';
import HowToLayout from '@/src/howTo/components/HowToLayout';
import {SB_SEARCH_QUERY} from '@/src/howTo/constants/search';
import prepareSearchedStories from '@/src/howTo/utils/prepareSearchedStories';
import debounce from '@/src/profile/aboutMe/utils/debounceFunction';

import css from '../styles/HowToLayout.module.css';

const PER_PAGE = 10;
const PAGE = 1;
const EXCLUDE_SIDE_BAR = SIDE_BAR;
const RESOLVE_URL = 'url';

const HowTo = ({children, sidebarStory, indexPage, resultPage, title}) => {
  const {asPath, push, locale, defaultLocale, query} = useRouter();
  const [searchFieldValues, setSearchFieldValues] = useState([]);
  const {isMaxWidth1100} = useIsMobile();

  const isBackButtonActive = Boolean(query?.[SB_SEARCH_QUERY.SEARCH]?.length);

  useEffect(() => {
    setSearchFieldValues([]);
  }, [asPath]);

  // @ts-ignore
  const handleOnSearchChange = debounce(async (event) => {
    const startWith =
      locale === defaultLocale
        ? indexPage.path.replace('/', '')
        : `${locale}${indexPage.path}`;

    const value = event.target.value;

    const query = {
      [SB_SEARCH_QUERY.PER_PAGE]: PER_PAGE,
      [SB_SEARCH_QUERY.PAGE]: PAGE,
      [SB_SEARCH_QUERY.SEARCH_TERMS]: value,
      [SB_SEARCH_QUERY.STARTS_WITH]: startWith,
      [SB_SEARCH_QUERY.EXCLUDING_SLUG]: EXCLUDE_SIDE_BAR,
      [SB_SEARCH_QUERY.RESOLVE_LINKS]: RESOLVE_URL,
    };

    if (!value.length) {
      return setSearchFieldValues([]);
    }

    try {
      // @ts-ignore
      const {data} = await requestStoryBlok({query});

      const preparedData = prepareSearchedStories(data.stories, value);

      setSearchFieldValues(preparedData);
    } catch (error) {
      setSearchFieldValues([]);
    }
  }, 500);

  const handleSearchSubmit = useCallback((event) => {
    event.preventDefault();

    if (!event?.target?.search?.value) {
      return;
    }

    event?.target?.search.blur();

    push({
      pathname: resultPage.path,
      query: {
        [SB_SEARCH_QUERY.SEARCH_TERMS]: event?.target?.search?.value || '',
      },
    });
  }, []);

  return (
    <HowToLayout
      searchFoundedItems={searchFieldValues}
      onSearchChange={handleOnSearchChange}
      sidebarStory={sidebarStory}
      onSearchSubmit={handleSearchSubmit}
      withBackButton={isBackButtonActive}
      title={title}
    >
      <div>
        {isMaxWidth1100 && isBackButtonActive && (
          <div className={css.mobileBackToList}>
            <BackToList />
          </div>
        )}
        {children}
      </div>
    </HowToLayout>
  );
};

HowTo.propTypes = {
  children: PropTypes.node.isRequired,
  sidebarStory: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
};

export default HowTo;
